/**
 * 🐟 **A fish with THICKNESS — T232, haugur's experiment.**
 *
 * 🎯 **haugur, 2026-09-17:** *"i want to do a experiment, i want to create
 * bleikja as a 3d model and put it into the ocean to see the differrence, it
 * need to have the sam spine and other mechanics as our fishes but i want to
 * judge the difference in apperance and feel, you get me ?"*
 *
 * ## 🔴 THE EXPERIMENT HAS ONE VARIABLE AND THIS FILE IS IT
 *
 * Everything else about a bleikja is the same as the other three fish: the same
 * spine (`seaFish` in `fishmind.ts`), the same two behaviours, the same water,
 * the same shoal budget, the same generated photograph, **and the same wave
 * bending it** ({@link swimOffset}). **The only thing that changes is that the
 * body has a cross-section instead of being two flat cards crossed in an X.**
 *
 * ## ✅ AND IT IS EXACTLY AS WIDE AS THE CARD ALREADY IS — that is the point
 *
 * 🔴 **The fattest point of this body is `FISH_ACROSS × card height`, which is
 * the width of the flat card, to the millimetre.** So the fish is not getting
 * fatter or slimmer; **it is getting ROUND.** ⚠️ **If this were built with its
 * own girth number, any difference haugur saw would have two possible causes
 * and the experiment would prove nothing.**
 *
 * 📏 **A sanity check worth keeping, because it is why 0.32 was right:** real
 * fish widths against ours — an 80 cm Atlantic cod is 9–10 cm across, and
 * `FISH_ACROSS × 0.309 m = 9.9 cm`. **The card was already the correct width.
 * It just had no depth in the third dimension.**
 *
 * ## 🔴 WHY IT IS NOT A SCULPTED, RIGGED MODEL
 *
 * 📖 [`Development/14_The_Animal_System.md`](../../../Development/14_The_Animal_System.md)
 * §③ — **D117 says no skeleton and no rigging pipeline**, and the objections are
 * not about money (D116 struck that argument out): **a rigged binary cannot be
 * read by anybody on this project** (D2), **our art loop generates photographs
 * rather than rigs**, so every re-roll of a look would cost the rigging again,
 * and **the exporters fail silently.**
 *
 * ✅ **So the body is arithmetic**, built the way Mojang builds a cod — read off
 * its own published files in
 * [`Minecraft/17`](../../../Development/Minecraft/17_Fishing_And_Fish.md) §5 —
 * and it tests on a workstation with no GPU, which is the only kind this project
 * has.
 *
 * ## 🔴 THE SILHOUETTE COMES FROM THE PHOTOGRAPH, NOT FROM A CURVE I INVENTED
 *
 * **The card gets its outline from the picture's own transparency.** So does
 * this: {@link profileFrom} reads the top and the bottom of the fish out of the
 * cut-out, station by station, and the body is lofted through those.
 *
 * 👉 **That is what keeps the comparison honest.** A hand-tuned body curve would
 * be a second thing that changed, and haugur would be judging my draughtsmanship
 * rather than thickness.
 *
 * ## ✅ AND THE FINS COME OUT FLAT FOR FREE, WHICH IS THE PART TO UNDERSTAND
 *
 * Each cross-section is an **ellipse**: widest at the fish's own midline and
 * narrowing to **nothing** at the top and the bottom of the silhouette.
 *
 * 🔴 **So the dorsal fin, the belly fin and the tail fan are all thin sheets
 * without a single line of code about fins**, because they are the parts of the
 * outline furthest from the midline. **That is Minecraft's own answer — a cod is
 * three boxes and three zero-thickness planes — arrived at by construction
 * rather than by modelling it.**
 *
 * ## ⚠️ What this module deliberately does NOT do
 *
 * No `three` import, no material, no colour — plain arrays, like
 * [`fishgeom.ts`](fishgeom.ts) and `driftgeom.ts`, **so it tests as arithmetic
 * without a browser or a GPU.**
 */

import { FISH_ACROSS, type FishShape } from './fishgeom.ts';

/**
 * 🐟 **The fish's own outline, read off its photograph.**
 *
 * ⚠️ **In the card's `v` coordinate, not in pixels** — 0 is the bottom of the
 * picture and 1 is the top, which is the convention `fishgeom.ts` already
 * uses and the reason the fish stopped swimming upside down (issue #651).
 *
 * 🔴 **So `top` is the fish's BACK and `bottom` is its BELLY**, and `top` is
 * always the larger number. Said as a sentence because the numbers look equally
 * right either way round.
 */
export interface FishProfile {
  /** The fish's back, per station along the body. */
  readonly top: Float32Array;
  /** The fish's belly, per station. */
  readonly bottom: Float32Array;
}

/**
 * 🐟 **Read the outline out of a cut-out fish.**
 *
 * @param rgba the decoded picture, four bytes a pixel
 * @param width the picture's width in pixels
 * @param height its height
 * @param stations how many slices along the body — **one more than the number
 *   of cuts**, so that it matches the card's rings exactly
 * @param alphaTest the same threshold the material uses, so this measures the
 *   picture somebody will actually see rather than one nobody will
 *
 * ## 🔴 THE MEDIAN, AND THE FIRST REASON WRITTEN HERE FOR IT WAS WRONG
 *
 * ⚠️ **This comment used to say the median was here to survive fraying.** 🔴 **It
 * was checked by swapping in an extreme, and the extreme PASSED** — because
 * `$theSILVERbellyPROBLEM` eats the belly *inward*, so the deepest column in a
 * window is the one the cutter did not touch. **Taking the extreme is immune to
 * exactly the fault the comment claimed it could not survive.**
 *
 * ✅ **THE REAL REASON IS BIAS, AND IT IS THE CLAMP LESSON AGAIN.** A window is
 * about twenty-five columns wide and **the fish's own outline SLOPES across
 * it** — steeply near the head and the tail. **The deepest column in that
 * window is not this station's depth; it is the depth of wherever the body
 * happens to be fattest nearby.** 🔴 **So an extreme fattens the fish
 * everywhere the outline is sloping**, which is precisely *measuring the thing
 * next to the thing*.
 *
 * ✅ **The median sits on the station.** ⚠️ **And it still resists fraying well
 * enough** — 📏 **two picture rows out of the eight the cutter takes** — which
 * is a benefit rather than the justification. **Both facts are tested, and the
 * one that proves the choice is that the profile recovers a KNOWN outline.**
 */
export function profileFrom(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  stations: number,
  alphaTest: number,
): FishProfile {
  const top = new Float32Array(stations);
  const bottom = new Float32Array(stations);

  /* eslint-disable security/detect-object-injection --
     Loop counters into typed arrays this function owns or was handed. The same
     reasoning `mesher.ts`, `textures.ts` and `fishmesh.ts` record for this rule. */
  /* Per column, where the fish begins and ends. -1 means no fish in it. */
  const colTop = new Int32Array(width).fill(-1);
  const colBot = new Int32Array(width).fill(-1);
  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      if (rgba[(y * width + x) * 4 + 3]! > alphaTest) {
        if (colTop[x]! < 0) colTop[x] = y;
        colBot[x] = y;
      }
    }
  }

  const median = (values: number[]): number => {
    values.sort((a, b) => a - b);
    return values[Math.floor(values.length / 2)]!;
  };

  /**
   * 🔴 **THE WINDOW IS CENTRED ON THE STATION, AND THE FIRST VERSION WAS NOT.**
   *
   * ⚠️ **A station sits at `s ÷ cuts`, which is `s ÷ (stations − 1)`** — the
   * first at the very nose and the last at the very tail tip. The first version
   * took the columns `[s ÷ stations, (s+1) ÷ stations)`, **which is the band
   * AFTER the station rather than the band AROUND it.**
   *
   * 🔴 **So every slice of the body was reading the outline from slightly
   * further back down the fish**, worst at the two ends: the nose took its depth
   * from behind the head, and the tail tip from in front of the fan. **It is
   * this repository's oldest fault — measuring the thing NEXT TO the thing.**
   *
   * ✅ **Found by a test about fraying**, which is not what it was written for.
   */
  const span = Math.max(1, width - 1);
  const halfWindow = Math.max(1, width / (2 * Math.max(1, stations - 1)));

  for (let s = 0; s < stations; s++) {
    const centre = (s / Math.max(1, stations - 1)) * span;
    /**
     * 🔴 **THE WINDOW SHRINKS AT THE ENDS, BUT IT NEVER SHRINKS TO NOTHING —
     * AND THAT SECOND HALF COST haugur A TAIL.**
     *
     * ⚠️ **A median is only unbiased if its window is symmetric**, so the
     * half-width is the smaller of what was asked for and what the picture can
     * give on BOTH sides. **That much is right and it stays.**
     *
     * 🔴 **BUT AT THE VERY LAST STATION IT CAME OUT ZERO, SO THE TAIL WAS READ
     * FROM A SINGLE COLUMN — THE RIGHTMOST PIXEL COLUMN IN THE PICTURE.**
     * 🎯 **haugur, issue #682, the first time he looked at one:** *"ummm where
     * is the rest of its tail"*.
     *
     * 📏 **Measured on the bleikja: the tail fan is 184 px deep at t = 0.984 and
     * still 182 at t = 0.997 — it does not taper at all. The very last column
     * reports 2 px**, because a forked tail's rearmost pixel is the tip of ONE
     * lobe. **So the body pinched a full-width fan shut into a point.**
     *
     * 👉 **A NOSE IS A POINT; THE TRAILING EDGE OF A FIN IS AN EDGE.** A window
     * of one column cannot tell those apart, and it read both as a point.
     *
     * ✅ **So: a floor of a quarter of a station step.** ⚠️ **It is one-sided at
     * the two ends and therefore slightly biased — deliberately.** A few pixels
     * of bias at the snout is a snout drawn a little blunt; **a zero-width
     * window at the tail is a fish with no tail**, and that is not a trade.
     */
    const half = Math.max(halfWindow / 2, Math.min(halfWindow, centre, span - centre));
    const from = Math.max(0, Math.round(centre - half));
    const to = Math.min(width, Math.max(from + 1, Math.round(centre + half) + 1));
    const tops: number[] = [];
    const bots: number[] = [];
    for (let x = from; x < to; x++) {
      if (colTop[x]! < 0) continue;
      tops.push(colTop[x]!);
      bots.push(colBot[x]!);
    }
    if (tops.length === 0) {
      /* 🔴 An empty station is a REAL possibility at the nose and the tail, and
         it must collapse to a point rather than to the middle of the picture —
         otherwise the body flares out to full depth at its own tip. */
      const mid = s === 0 ? 0.5 : (top[s - 1]! + bottom[s - 1]!) / 2;
      top[s] = mid;
      bottom[s] = mid;
      continue;
    }
    /* ⚠️ Row 0 is the TOP of the picture and v = 1 is the top of the card, so
       the two run opposite ways. See {@link FishProfile}. */
    const rows = Math.max(1, height - 1);
    top[s] = 1 - median(tops) / rows;
    bottom[s] = 1 - median(bots) / rows;
  }
  /* eslint-enable security/detect-object-injection */

  return { top, bottom };
}

/**
 * 🐟 **How fat the fish is at this point along its length, 0 to 1.**
 *
 * 🔴 **A fish is fattest just behind the head, not in the middle**, and it
 * tapers to nothing at both ends. **This one curve is what makes the tail fan
 * and the dorsal fin come out as thin sheets** while the shoulder stays round.
 *
 * 📏 **Peaks at exactly t = 0.25, at exactly 1.** ⚠️ **A dial for haugur's
 * eye**, not a measurement — where the shoulder sits is what makes a fish read
 * as a fish rather than as a cigar, and no instrument here can judge that.
 *
 * 🔴 **THE SQUARE ROOT IS WHY THE PEAK IS A ROUND NUMBER, AND THAT IS
 * DELIBERATE.** An earlier version used `t^0.55`, which peaks at 0.2836 — so
 * with eight cuts **no station ever landed on the widest point** and the body
 * came out 0.8% narrower than the card. ⚠️ **0.2 mm on a 56 cm fish, and
 * invisible on a half-metre fish** — but the header promises the body is as
 * wide as the card, and a
 * promise nobody can check by eye has to be exactly true in the arithmetic.
 * ✅ **`√t` peaks where `√t = ½`, which is a quarter of the way along**, and a
 * quarter is a station.
 *
 * @param t 0 at the nose, 1 at the tail tip
 */
export function girth(t: number): number {
  if (t <= 0 || t >= 1) return 0;
  return Math.sin(Math.PI * Math.sqrt(t)) ** 1.4;
}

/**
 * ☀️ **How brightly lit a piece of the fish's skin is, 0.55 to 1.**
 *
 * 🔴 **THE FLOOR IS NOT TIMIDITY — IT IS ISSUE #647.** 🎯 **haugur:** *"Light
 * and shadow affect the fish in a weird way"*, and his picture showed **half a
 * shoal almost black** in the same daylight at the same depth. ⚠️ **The cause
 * was a Lambert material lighting a crossed card by a faked normal**, and on a
 * double-sided face the normal flips, so which half of the shoal went black
 * depended on where he stood.
 *
 * ✅ **A solid body has REAL outward normals, so it can be lit honestly** — and
 * **the floor guarantees no part of a fish is ever darker than 55%**, whatever
 * the normal does. 🔴 **That is the fault designed out rather than found
 * afterwards.**
 *
 * ⚠️ **The light comes from ABOVE AND SLIGHTLY AHEAD**, because it is under
 * water and the sun is overhead: a fish's back is lit and its belly is not,
 * which is the whole reason real fish are dark on top and pale underneath.
 *
 * @param nx the surface normal, already turned to face the way the fish is going
 */
export function shadeFor(nx: number, ny: number, nz: number): number {
  /* A fixed underwater key light. ⚠️ **Not the scene's sun** — this is baked
     arithmetic a test can assert, and it cannot be switched off by a renderer
     setting the way a real light can. */
  const lx = 0.28;
  const ly = 0.93;
  const lz = 0.24;
  const lambert = Math.max(0, nx * lx + ny * ly + nz * lz);
  return 0.55 + 0.45 * lambert;
}

/**
 * 🐟 **How many points go round the body.**
 *
 * ⚠️ **Ten.** 📏 At the range a fish is met — 60 px on screen at best — the
 * silhouette is smooth at eight and indistinguishable at sixteen. 🔴 **A body
 * is 90 vertices against the card's 36**, and at the bleikja's share of a
 * 60-fish budget that is about 900 vertices a frame, **against a chunk mesher
 * on the same thread building 239,492 faces** (T218, T230).
 */
export const BODY_RINGS = 10;

/**
 * 🐟 **Where the body ends and the TAIL FIN begins — the fish's waist.**
 *
 * 🔴 **THIS EXISTS BECAUSE A FORKED TAIL CANNOT BE A CROSS-SECTION AT ALL**, and
 * issue #682 is what proved it. 🎯 **haugur:** *"ummm where is the rest of its
 * tail"*.
 *
 * ⚠️ **A lofted body reads one top-and-bottom per slice.** That works for a
 * body. **A forked tail is TWO LOBES with a gap**, and at the very back only one
 * lobe survives — 📏 **on the lax the outline drops from 214 px to 81 in a
 * single step at t = 0.97, because its upper lobe reaches further than its
 * lower.** 🔴 **So a lofted tail does not merely pinch: its CENTRE follows the
 * surviving lobe, and the fish's axis bends upward at the back.**
 *
 * ✅ **The answer was already in the research.** `Minecraft/17` §5, read off
 * Mojang's own files: **a cod is three boxes and three ZERO-THICKNESS PLANES**,
 * and `tailfin` is one of the planes. **A fin is flat. It always was.**
 *
 * 👉 **So the body lofts to the waist, and the fin is a flat sheet that takes
 * its shape from the photograph's transparency** — exactly how the whole card
 * fish used to work, kept for the one part of a fish it is right for. **The fork
 * then comes out perfect on every fish, at any resolution, for free.**
 *
 * @returns the station index at which the body stops and the fin starts
 */
export function tailWaist(profile: FishProfile): number {
  const stations = profile.top.length;
  /* eslint-disable security/detect-object-injection --
     Loop counters into typed arrays this module built. */
  const depth = (s: number): number => profile.top[s]! - profile.bottom[s]!;
  /* 🔴 Looked for only in the BACK HALF. A fish is narrow at the nose too, and
     the shallowest slice overall is the snout — which would put the waist at
     the front and make the whole fish a fin. */
  const from = Math.max(1, Math.round((stations - 1) * 0.55));
  const to = Math.max(from + 1, Math.round((stations - 1) * 0.95));
  let waist = from;
  let least = Infinity;
  for (let s = from; s <= to && s < stations; s++) {
    if (depth(s) < least) {
      least = depth(s);
      waist = s;
    }
  }
  /* eslint-enable security/detect-object-injection */
  return waist;
}

/**
 * 🐟 **Build the fish as a solid body.**
 *
 * @param length nose to tail, in metres
 * @param aspect the picture's width ÷ height — **the same number the card
 *   uses**, so the body is exactly as deep as the card is
 * @param profile the fish's own outline, from {@link profileFrom}
 * @param cuts how many divisions along the body. ⚠️ **Must match the profile's
 *   stations minus one**, or the outline is stretched along the wrong body
 * @param rings how many points round each cross-section
 * @param across the width at the fattest point, as a share of the card's
 *   height — 🔴 **defaults to the card's own {@link FISH_ACROSS}, deliberately.
 *   See the header: the body is as wide as the card, never wider**
 */
export function buildFishBody(
  length: number,
  aspect: number,
  profile: FishProfile,
  cuts = 8,
  rings = BODY_RINGS,
  across = FISH_ACROSS,
): FishShape {
  const stations = cuts + 1;
  if (profile.top.length !== stations) {
    throw new Error(
      `profile has ${profile.top.length} stations but ${stations} are needed for ${cuts} cuts`,
    );
  }

  const height = length / aspect;
  /* 🔴 The HALF-width at the fattest point. `across` is a full width against the
     card's height, so halving it here is what makes the body and the flat card
     measure the same across. See the header. */
  let maxHalfWidth = (across * height) / 2;

  /**
   * 🔴 **A TEN-SIDED RING NEVER REACHES ITS OWN WIDEST POINT, AND THE TEST
   * CAUGHT IT.** The ring samples `sin θ` at multiples of 36°, and the nearest
   * to 90° is 72°, so **the widest vertex sat at 95.1% of the card's width** —
   * 2.8 mm narrow on a 56 cm fish.
   *
   * ⚠️ **Invisible, and it still had to go**, because the header claims the body
   * is as wide as the card *to the millimetre* and haugur has been told the same
   * in plain English. **A claim nobody can check by eye is exactly the kind that
   * has to be true in the arithmetic.**
   *
   * ✅ **So the ring is scaled until its widest VERTEX lands on the card's
   * width.** Nothing bulges past it: every edge between two vertices is a chord,
   * and a chord lies inside.
   *
   * 👉 **Why not simply turn the ring half a step so a vertex sits at 90°?**
   * Because then no vertex sits at 0°, and **the top of the body would fall 5%
   * short of the photograph's own outline** — which breaks the more important
   * invariant. **The silhouette from the side is the view a fish is judged in.**
   */
  let reach = 0;
  for (let r = 0; r < rings; r++) {
    reach = Math.max(reach, Math.abs(Math.sin((r / rings) * Math.PI * 2)));
  }
  if (reach > 1e-6) maxHalfWidth /= reach;

  const count = stations * rings;
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const along = new Float32Array(count);
  const uvs = new Float32Array(count * 2);
  const indices = new Uint32Array(cuts * rings * 6);

  /**
   * 🔴 **THE BODY STOPS AT THE WAIST AND THE TAIL FIN IS A FLAT SHEET** — see
   * {@link tailWaist}, and issue #682 for why.
   *
   * ⚠️ **The fin's rectangle is the WHOLE fan's extent**, taken across every
   * station behind the waist rather than per slice. **The photograph's
   * transparency cuts the fork out of it**, which is the one thing a cut-out
   * does better than any geometry — and it is how the card drew the whole fish.
   */
  const waist = tailWaist(profile);
  let finTop = -Infinity;
  let finBot = Infinity;
  /* eslint-disable security/detect-object-injection -- loop counters, as above. */
  for (let s = waist; s < stations; s++) {
    finTop = Math.max(finTop, profile.top[s]!);
    finBot = Math.min(finBot, profile.bottom[s]!);
  }
  /* eslint-enable security/detect-object-injection */

  for (let s = 0; s < stations; s++) {
    const t = s / cuts;
    const inFin = s > waist;
    /* eslint-disable security/detect-object-injection --
       Loop counters into typed arrays this function owns, the same reasoning
       `mesher.ts` and `fishmesh.ts` record for the same rule. */
    const vTop = inFin ? finTop : profile.top[s]!;
    const vBot = inFin ? finBot : profile.bottom[s]!;
    /* ⚠️ The card puts picture-row `v` at y = (v − 0.5) × height. The body has
       to agree exactly, or the two fish are different sizes and the experiment
       compares two things. */
    const yTop = (vTop - 0.5) * height;
    const yBot = (vBot - 0.5) * height;
    const centre = (yTop + yBot) / 2;
    const halfDepth = (yTop - yBot) / 2;
    /* 🔴 A fin has NO thickness. `Minecraft/17` §5: `tailfin` is a plane. */
    const halfWidth = inFin ? 0 : maxHalfWidth * girth(t);

    for (let r = 0; r < rings; r++) {
      const theta = (r / rings) * Math.PI * 2;
      const c = Math.cos(theta);
      const sn = Math.sin(theta);
      const y = centre + halfDepth * c;
      const z = halfWidth * sn;
      const i = s * rings + r;
      positions[i * 3] = t * length;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      along[i] = t;
      uvs[i * 2] = t;
      /* 🔴 Straight back out of the picture: the row this point came from. So
         the back of the fish wears the back of the photograph wherever it sits
         round the body, and both flanks wear the same flank — which is what a
         real fish looks like and costs nothing. */
      uvs[i * 2 + 1] = y / height + 0.5;
    }
    /* eslint-enable security/detect-object-injection */
  }

  /* ── faces ─────────────────────────────────────────────────────────────── */
  let n = 0;
  for (let s = 0; s < cuts; s++) {
    for (let r = 0; r < rings; r++) {
      const a = s * rings + r;
      const b = s * rings + ((r + 1) % rings);
      const c = (s + 1) * rings + r;
      const d = (s + 1) * rings + ((r + 1) % rings);
      /* eslint-disable security/detect-object-injection -- see above. */
      indices[n] = a;
      indices[n + 1] = c;
      indices[n + 2] = b;
      indices[n + 3] = b;
      indices[n + 4] = c;
      indices[n + 5] = d;
      /* eslint-enable security/detect-object-injection */
      n += 6;
    }
  }

  fillNormals(positions, normals, stations, rings);

  /**
   * 🔆 **THE FIN IS LIT LIKE A CARD, NOT LIKE A WALL.**
   *
   * ⚠️ **A flat sheet standing on edge has a normal pointing sideways**, so the
   * overhead light misses it completely and {@link shadeFor} returns its floor —
   * **a tail noticeably darker than the fish it belongs to**, with a hard seam at
   * the waist.
   *
   * ✅ **So every fin vertex takes an UP normal**, which is `Minecraft/13`'s
   * *shade: false* trick and exactly what the crossed cards already do. **A tail
   * fin is thin and catches light from every side; flat and bright is closer to
   * the truth than a dark wall is.**
   */
  /* ⚠️ **From waist + 1, not from the waist.** The waist station is the last
     slice of the BODY and keeps its own outward normal; only the flat sheet
     behind it is lit like a card. 🔴 **This read `s = waist` for one run and a
     test caught it** — the body's last ring was lit as though it were a fin. */
  for (let s = waist + 1; s < stations; s++) {
    for (let r = 0; r < rings; r++) {
      /* eslint-disable security/detect-object-injection -- loop counters. */
      const i = (s * rings + r) * 3;
      normals[i] = 0;
      normals[i + 1] = 1;
      normals[i + 2] = 0;
      /* eslint-enable security/detect-object-injection */
    }
  }

  return { positions, normals, along, uvs, indices, vertexCount: count };
}

/**
 * 🐟 **The outward normal at every point, worked out from the body itself.**
 *
 * 🔴 **Not from the ellipse's formula, and that is deliberate.** A formula would
 * be right about the ellipse and wrong about the **taper** — the body narrows
 * along its length as well as round its ring, and a normal that ignores that
 * lights the nose and the tail as though they were a cylinder.
 *
 * ✅ **So: the cross product of the two directions the surface actually runs
 * in**, measured from its own vertices. ⚠️ **It is right by construction and it
 * cannot disagree with the geometry**, which is the same reason the fish's pitch
 * is measured from how it moved rather than sent.
 *
 * ⚠️ **A degenerate ring — the nose and the tail tip, where the width is zero —
 * has no cross product.** Those points take their neighbour's normal rather than
 * a zero, because a zero-length normal shades black and **black at the nose is
 * exactly the #647 failure wearing a different hat.**
 */
function fillNormals(
  positions: Float32Array,
  normals: Float32Array,
  stations: number,
  rings: number,
): void {
  const at = (s: number, r: number, k: number): number =>
    positions[(s * rings + ((r + rings) % rings)) * 3 + k]!;

  for (let s = 0; s < stations; s++) {
    const prev = Math.max(0, s - 1);
    const next = Math.min(stations - 1, s + 1);
    for (let r = 0; r < rings; r++) {
      /* Round the ring, and along the body. */
      const ax = at(s, r + 1, 0) - at(s, r - 1, 0);
      const ay = at(s, r + 1, 1) - at(s, r - 1, 1);
      const az = at(s, r + 1, 2) - at(s, r - 1, 2);
      const bx = at(next, r, 0) - at(prev, r, 0);
      const by = at(next, r, 1) - at(prev, r, 1);
      const bz = at(next, r, 2) - at(prev, r, 2);
      let nx = ay * bz - az * by;
      let ny = az * bx - ax * bz;
      let nz = ax * by - ay * bx;
      const len = Math.hypot(nx, ny, nz);
      const i = s * rings + r;
      if (len < 1e-9) {
        /* 🔴 A degenerate point. Point it straight out from the axis instead,
           which is never zero and never black. */
        const y = at(s, r, 1);
        const z = at(s, r, 2);
        const out = Math.hypot(y, z) || 1;
        normals[i * 3] = 0;
        normals[i * 3 + 1] = y / out;
        normals[i * 3 + 2] = z / out;
        continue;
      }
      nx /= len;
      ny /= len;
      nz /= len;
      /* ⚠️ Make it point AWAY from the body's own axis. The cross product's sign
         depends on the winding, and a body lit inside-out is the #647 fault
         again — so it is fixed here rather than trusted. */
      const y = at(s, r, 1);
      const z = at(s, r, 2);
      if (ny * y + nz * z < 0) {
        nx = -nx;
        ny = -ny;
        nz = -nz;
      }
      normals[i * 3] = nx;
      normals[i * 3 + 1] = ny;
      normals[i * 3 + 2] = nz;
    }
  }
}
